import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Ticket, Plus, Edit2, Trash2, Package, AlertTriangle, ArrowLeft, ClipboardList, GripVertical, Download, FileText, FileSpreadsheet, Loader2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LottoMeldeVerlauf from './LottoMeldeVerlauf';

const API = process.env.REACT_APP_BACKEND_URL;

export const LottoProduktVerwaltung = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRestockDialog, setShowRestockDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('verwaltung'); // NEW: Tab state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pendingMap, setPendingMap] = useState({}); // productId → total pending packages
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth  = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
  const [exportForm, setExportForm] = useState({
    start_date: firstOfMonth,
    end_date:   lastOfMonth,
    format:     'pdf',
    location_id: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    stock: '',
    warning_threshold: '5',
    package_number: '',
    enabled_for_reporting: false,
    count_direction: 'down',
  });

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const [prodRes, ordersRes] = await Promise.all([
        axios.get(`${API}/api/lotto/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/lotto/orders?status=pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setProducts(prodRes.data);
      // Build pendingMap: productId → sum of ordered_packages across all pending orders
      const map = {};
      (ordersRes.data || []).forEach(order => {
        (order.items || []).forEach(item => {
          map[item.product_id] = (map[item.product_id] || 0) + (item.ordered_packages || 0);
        });
      });
      setPendingMap(map);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.name || !formData.price || !formData.stock) {
        toast.error('Bitte alle Pflichtfelder ausfüllen');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/lotto/products`, {
        name: formData.name,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        warning_threshold: parseInt(formData.warning_threshold),
        package_number: formData.package_number || null,
        enabled_for_reporting: formData.enabled_for_reporting,
        count_direction: formData.count_direction || 'down',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Produkt erfolgreich erstellt');
      setShowCreateDialog(false);
      setFormData({ name: '', price: '', stock: '', warning_threshold: '5', package_number: '', enabled_for_reporting: false, count_direction: 'down' });
      fetchProducts();
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/lotto/products/${selectedProduct.id}`, {
        name: formData.name,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        warning_threshold: parseInt(formData.warning_threshold),
        package_number: formData.package_number || null,
        enabled_for_reporting: formData.enabled_for_reporting,
        count_direction: formData.count_direction || 'down',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Produkt aktualisiert');
      setShowEditDialog(false);
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/lotto/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Produkt gelöscht');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleRestock = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/lotto/products/${selectedProduct.id}/restock`, {
        added_stock: parseInt(formData.stock),
        package_number: formData.package_number || null,
        enabled_for_reporting: formData.enabled_for_reporting
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Nachschub erfolgreich hinzugefügt');
      setShowRestockDialog(false);
      setSelectedProduct(null);
      setFormData({ name: '', price: '', stock: '', warning_threshold: '5', package_number: '', enabled_for_reporting: false });
      fetchProducts();
    } catch (error) {
      console.error('Error restocking:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Nachschub');
    }
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    // Up-Modus: "Bestand" = feste Paketgröße (stock_initial), nicht der aktuelle Rest-Bestand
    const bestandValue = product.count_direction === 'up'
      ? (product.stock_initial ?? product.stock)
      : product.stock;
    setFormData({
      name: product.name,
      price: product.price.toString(),
      stock: bestandValue.toString(),
      warning_threshold: product.warning_threshold.toString(),
      package_number: product.package_number || '',
      enabled_for_reporting: product.enabled_for_reporting || false,
      count_direction: product.count_direction || 'down',
    });
    setShowEditDialog(true);
  };

  const openRestockDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      stock: '',
      warning_threshold: product.warning_threshold.toString(),
      package_number: '',
      enabled_for_reporting: product.enabled_for_reporting || false,
      count_direction: product.count_direction || 'down',
    });
    setShowRestockDialog(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        format:     exportForm.format,
        start_date: exportForm.start_date,
        end_date:   exportForm.end_date,
      });
      if (exportForm.location_id) params.append('location_id', exportForm.location_id);

      const response = await axios.get(
        `${API}/api/lotto/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );

      const mimeType = exportForm.format === 'pdf' ? 'application/pdf' : 'text/csv';
      const ext      = exportForm.format === 'pdf' ? 'pdf' : 'csv';
      const filename = `lotto_bericht_${exportForm.start_date}_${exportForm.end_date}.${ext}`;
      const url      = URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link     = document.createElement('a');
      link.href      = url;
      link.download  = filename;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(`${exportForm.format.toUpperCase()} erfolgreich heruntergeladen`);
      setShowExportDialog(false);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Fehler beim Export. Bitte erneut versuchen.');
    } finally {
      setExporting(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = products.findIndex((p) => p.id === active.id);
      const newIndex = products.findIndex((p) => p.id === over.id);

      const newProducts = arrayMove(products, oldIndex, newIndex);
      setProducts(newProducts);

      // Save new order to backend
      try {
        const token = localStorage.getItem('token');
        const productIds = newProducts.map(p => p.id);
        
        await axios.put(`${API}/api/lotto/products/reorder`, 
          { product_ids: productIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Reihenfolge gespeichert');
      } catch (error) {
        console.error('Error saving order:', error);
        toast.error('Fehler beim Speichern der Reihenfolge');
        // Revert on error
        fetchProducts();
      }
    }
  };

  const getStockStatus = (product) => {
    const stock = product.stock;
    const threshold = product.warning_threshold;

    if (stock === 0) return { color: 'bg-gray-500', text: 'Ausverkauft', icon: '⚫' };
    if (stock < threshold) return { color: 'bg-red-500', text: 'Kritisch', icon: '🔴' };
    if (stock === threshold) return { color: 'bg-yellow-500', text: 'Niedrig', icon: '🟡' };
    return { color: 'bg-green-500', text: 'Gut', icon: '🟢' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600 dark:text-gray-400">Lade...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Lotto Verwaltung</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Produkte verwalten und Bestand melden</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('verwaltung')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'verwaltung'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Package className="h-4 w-4" />
              Produktverwaltung
            </button>
            <button
              onClick={() => setActiveTab('melden')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'melden'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ClipboardList className="h-4 w-4" />
              Bestand melden
            </button>
            <button
              onClick={() => setActiveTab('verlauf')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'verlauf'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid="tab-melde-verlauf"
            >
              <Clock className="h-4 w-4" />
              Melde-Verlauf
            </button>
            <button
              onClick={() => navigate('/admin/lotto/rubellose')}
              className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2"
            >
              <Ticket className="h-4 w-4" />
              Bestellungen
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'verwaltung' ? (
          <>
            {/* Produktverwaltung Content */}
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button
                  data-testid="lotto-export-btn"
                  variant="outline"
                  onClick={() => setShowExportDialog(true)}
                  className="border-[#094199] text-[#094199] hover:bg-[#e8f0fe]"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button onClick={() => {
                  setFormData({ name: '', price: '', stock: '', warning_threshold: '5', package_number: '', enabled_for_reporting: false, count_direction: 'down' });
                  setShowCreateDialog(true);
                }} className="flex items-center gap-2" data-testid="lotto-create-btn">
                  <Plus className="h-4 w-4" />
                  Neues Produkt
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                💡 Ziehe die Karten, um die Reihenfolge zu ändern
              </p>
            </div>

        {/* Products List with Drag & Drop */}
        {products.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Ticket className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-center">Noch keine Produkte vorhanden</p>
              <Button onClick={() => {
                setFormData({ name: '', price: '', stock: '', warning_threshold: '5', package_number: '', enabled_for_reporting: false, count_direction: 'down' });
                setShowCreateDialog(true);
              }} className="mt-4">
                Erstes Produkt anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={products.map(p => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => (
                  <SortableProductCard 
                    key={product.id} 
                    product={product}
                    getStockStatus={getStockStatus}
                    openEditDialog={openEditDialog}
                    openRestockDialog={openRestockDialog}
                    handleDelete={handleDelete}
                    pendingPackages={pendingMap[product.id] || 0}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
          </>
        ) : activeTab === 'melden' ? (
          /* Bestand melden Tab */
          <AdminLottoReporting pendingMap={pendingMap} />
        ) : (
          /* Melde-Verlauf Tab */
          <LottoMeldeVerlauf />
        )}

        {/* Dialogs (Create, Edit, Restock) */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neues Rubbellos anlegen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Lucky 7"
                />
              </div>
              <div>
                <Label htmlFor="price">Preis (€) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="5.00"
                />
              </div>
              <div>
                <Label htmlFor="stock">Bestand (Stück) *</Label>
                <Input
                  id="stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  placeholder="20"
                />
              </div>
              <div>
                <Label htmlFor="threshold">Warnschwelle (Stück) *</Label>
                <Input
                  id="threshold"
                  type="number"
                  value={formData.warning_threshold}
                  onChange={(e) => setFormData({ ...formData, warning_threshold: e.target.value })}
                  placeholder="5"
                />
              </div>
              <div>
                <Label htmlFor="package">Paketnummer (optional)</Label>
                <Input
                  id="package"
                  value={formData.package_number}
                  onChange={(e) => setFormData({ ...formData, package_number: e.target.value })}
                  placeholder="XX-XX-XXX-XXXXXX-XXX-XX"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Format: 12-34-001-123456-020-08</p>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="reporting"
                  checked={formData.enabled_for_reporting}
                  onChange={(e) => setFormData({ ...formData, enabled_for_reporting: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                />
                <Label htmlFor="reporting" className="cursor-pointer">
                  Für Mitarbeiter-Reporting freischalten
                </Label>
              </div>
              {/* Zählrichtung — Create */}
              <div className="space-y-1.5 pt-2">
                <Label className="text-sm font-medium">Zählrichtung</Label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="count_direction_create" value="down"
                      checked={(formData.count_direction || 'down') === 'down'}
                      onChange={() => setFormData({ ...formData, count_direction: 'down' })}
                      className="accent-blue-600" />
                    <span className="text-sm">Von Bestand abwärts <span className="text-xs text-muted-foreground">(Standard: von 50 → 0)</span></span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="count_direction_create" value="up"
                      checked={formData.count_direction === 'up'}
                      onChange={() => setFormData({ ...formData, count_direction: 'up' })}
                      className="accent-blue-600" />
                    <span className="text-sm">Von 1 aufwärts <span className="text-xs text-muted-foreground">(Reihenfolge: 1 → Bestand)</span></span>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleCreate}>Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Produkt bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-price">Preis (€) *</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-stock">Bestand (Stück) *</Label>
                <Input
                  id="edit-stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-threshold">Warnschwelle (Stück) *</Label>
                <Input
                  id="edit-threshold"
                  type="number"
                  value={formData.warning_threshold}
                  onChange={(e) => setFormData({ ...formData, warning_threshold: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-package">Paketnummer (optional)</Label>
                <Input
                  id="edit-package"
                  value={formData.package_number}
                  onChange={(e) => setFormData({ ...formData, package_number: e.target.value })}
                  placeholder="XX-XX-XXX-XXXXXX-XXX-XX"
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="edit-reporting"
                  checked={formData.enabled_for_reporting}
                  onChange={(e) => setFormData({ ...formData, enabled_for_reporting: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                />
                <Label htmlFor="edit-reporting" className="cursor-pointer">
                  Für Mitarbeiter-Reporting freischalten
                </Label>
              </div>
              {/* Zählrichtung */}
              <div className="space-y-1.5 pt-2">
                <Label className="text-sm font-medium">Zählrichtung</Label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="count_direction_edit" value="down"
                      checked={(formData.count_direction || 'down') === 'down'}
                      onChange={() => setFormData({ ...formData, count_direction: 'down' })}
                      className="accent-blue-600" />
                    <span className="text-sm">Von Bestand abwärts <span className="text-xs text-muted-foreground">(Standard: von 50 → 0)</span></span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="count_direction_edit" value="up"
                      checked={formData.count_direction === 'up'}
                      onChange={() => setFormData({ ...formData, count_direction: 'up' })}
                      className="accent-blue-600" />
                    <span className="text-sm">Von 1 aufwärts <span className="text-xs text-muted-foreground">(Reihenfolge: 1 → Bestand)</span></span>
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleUpdate}>Aktualisieren</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restock Dialog */}
        <Dialog open={showRestockDialog} onOpenChange={setShowRestockDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Neues Paket erfassen
              </DialogTitle>
            </DialogHeader>
            {selectedProduct && (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">{selectedProduct.name}</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Bestand: 0 Stück – bereit für neues Paket</p>
                  {selectedProduct.package_number && (
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                      Altes Paket: <span className="font-mono">{selectedProduct.package_number}</span>
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="restock-amount">Anzahl Stück im neuen Paket *</Label>
                  <Input
                    id="restock-amount"
                    type="number"
                    min="1"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="z.B. 20"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="restock-package">Neue Paketnummer (optional)</Label>
                  <Input
                    id="restock-package"
                    value={formData.package_number}
                    onChange={(e) => setFormData({ ...formData, package_number: e.target.value })}
                    placeholder="Paketnummer eingeben"
                    className="font-mono text-sm mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Beliebiges Format – z.B. 12-34-567-890123-456-78</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="restock-reporting"
                    checked={formData.enabled_for_reporting}
                    onChange={(e) => setFormData({ ...formData, enabled_for_reporting: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <Label htmlFor="restock-reporting" className="cursor-pointer text-sm">
                    Für Mitarbeiter-Reporting freischalten
                  </Label>
                </div>
                <p className="text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-2">
                  ✓ Das Produkt wird automatisch wieder aktiviert
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRestockDialog(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={handleRestock}
                disabled={!formData.stock || parseInt(formData.stock) <= 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Package className="h-4 w-4 mr-2" />
                Neues Paket speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Modal */}
        <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="h-5 w-5 text-[#094199]" />
                Lotto Verkaufsbericht exportieren
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="export-start">Von</Label>
                  <Input
                    id="export-start"
                    type="date"
                    data-testid="export-start-date"
                    value={exportForm.start_date}
                    onChange={(e) => setExportForm({ ...exportForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="export-end">Bis</Label>
                  <Input
                    id="export-end"
                    type="date"
                    data-testid="export-end-date"
                    value={exportForm.end_date}
                    onChange={(e) => setExportForm({ ...exportForm, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Format</Label>
                <div className="flex gap-3 mt-2">
                  <button
                    data-testid="export-format-pdf"
                    onClick={() => setExportForm({ ...exportForm, format: 'pdf' })}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-colors text-sm font-medium ${
                      exportForm.format === 'pdf'
                        ? 'border-[#094199] bg-[#e8f0fe] text-[#094199]'
                        : 'border-border text-muted-foreground hover:border-[#094199]'
                    }`}
                  >
                    <FileText className="h-4 w-4" />
                    PDF
                  </button>
                  <button
                    data-testid="export-format-csv"
                    onClick={() => setExportForm({ ...exportForm, format: 'csv' })}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-colors text-sm font-medium ${
                      exportForm.format === 'csv'
                        ? 'border-[#094199] bg-[#e8f0fe] text-[#094199]'
                        : 'border-border text-muted-foreground hover:border-[#094199]'
                    }`}
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    CSV (Excel)
                  </button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
                Der Bericht enthält: Produktname, Preis, verkaufte Stück,
                Umsatz und Bestandsabweichungen im gewählten Zeitraum.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExportDialog(false)}>
                Abbrechen
              </Button>
              <Button
                data-testid="export-download-btn"
                onClick={handleExport}
                disabled={exporting}
                className="bg-[#094199] hover:bg-[#0a3580]"
              >
                {exporting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Erstelle...</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" /> Herunterladen</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

// Admin Lotto Reporting Component (gleich wie Employee)
const AdminLottoReporting = ({ pendingMap = {} }) => {
  const [products, setProducts] = useState([]);
  const [newStocks, setNewStocks] = useState({});
  const [originalStocks, setOriginalStocks] = useState({});
  const [loadedPositions, setLoadedPositions] = useState({}); // gespeicherte Pos nach letztem Fetch
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // iOS-Ghost-Click: 1 Tap → sonst 2× Handler. Guard: gleiche Aktion+Produkt <400ms ignorieren.
  const lastTapRef = useRef({});
  const dedupeTap = (key) => {
    const now = Date.now();
    if (now - (lastTapRef.current[key] || 0) < 400) return false;
    lastTapRef.current[key] = now;
    return true;
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Für "up": Pos aus gespeichertem Bestand ableiten (damit Counter nach Speichern nicht auf 1 springt)
  const getUpInitPos = (stock, stockInitial) => {
    const maxS = stockInitial || stock;
    if (stock <= 0) return maxS + 1;   // ausverkauft: alle verkauft → Position hinter der letzten Karte
    if (stock >= maxS) return 1;        // frisch: nichts verkauft → Karte 1
    return maxS - stock + 1;            // aktuelle Losnummer = verkauft + 1
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/lotto/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const prods = response.data || [];
      setProducts(prods);
      
      const initialStocks = {};
      const origStocks = {};
      const loadedPos = {};
      prods.forEach(p => {
        const maxStock = p.stock_initial || p.stock;
        if (p.count_direction === 'up') {
          const pos = getUpInitPos(p.stock, p.stock_initial || p.stock);
          initialStocks[p.id] = pos;
          loadedPos[p.id] = pos; // merkt was geladen wurde
        } else {
          initialStocks[p.id] = p.stock;
        }
        origStocks[p.id] = maxStock;
      });
      setNewStocks(initialStocks);
      setOriginalStocks(origStocks);
      setLoadedPositions(loadedPos);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const handleIncrement = (productId) => {
    if (!dedupeTap(`${productId}:inc`)) return;
    const product = products.find(p => p.id === productId);
    const dir = product?.count_direction === 'up' ? 'up' : 'down';
    const maxStock = originalStocks[productId] || 0;
    const maxPos = dir === 'up' ? maxStock + 1 : maxStock;
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.min(maxPos, (prev[productId] || (dir === 'up' ? 1 : 0)) + 1)
    }));
  };

  const handleDecrement = (productId) => {
    if (!dedupeTap(`${productId}:dec`)) return;
    const prod = products.find(p => p.id === productId);
    const minPos = prod?.count_direction === 'up' ? (loadedPositions[productId] ?? 1) : 0;
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.max(minPos, (prev[productId] || 0) - 1)
    }));
  };

  const handleIncrement5 = (productId) => {
    if (!dedupeTap(`${productId}:inc5`)) return;
    const product = products.find(p => p.id === productId);
    const dir = product?.count_direction === 'up' ? 'up' : 'down';
    const maxStock = originalStocks[productId] || 0;
    const maxPos = dir === 'up' ? maxStock + 1 : maxStock;
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.min(maxPos, (prev[productId] || (dir === 'up' ? 1 : 0)) + 5)
    }));
  };

  const handleDecrement5 = (productId) => {
    if (!dedupeTap(`${productId}:dec5`)) return;
    const prod = products.find(p => p.id === productId);
    const minPos = prod?.count_direction === 'up' ? (loadedPositions[productId] ?? 1) : 0;
    setNewStocks(prev => ({
      ...prev,
      [productId]: Math.max(minPos, (prev[productId] || 0) - 5)
    }));
  };

  const getDifference = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;
    if (product.count_direction === 'up') {
      const pos = newStocks[productId] ?? 1;
      const loaded = loadedPositions[productId] ?? 1;
      return Math.max(0, pos - loaded); // neu verkauft seit letztem Laden
    }
    return (newStocks[productId] || 0) - product.stock;
  };

  const hasChanges = () => {
    return Object.entries(newStocks).some(([productId, pos]) => {
      const product = products.find(p => p.id === productId);
      if (!product) return false;
      if (product.count_direction === 'up') {
        const loaded = loadedPositions[productId] ?? 1;
        return pos > loaded; // Änderung nur wenn User weiter gezählt als zuletzt geladen
      }
      return pos !== product.stock;
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const batchId = (window.crypto && window.crypto.randomUUID)
        ? `BATCH-${window.crypto.randomUUID()}`
        : `BATCH-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      // ALLE Lose in EINER Meldung (auch unverändert / 0 verkauft) → vollständiger Verlauf
      const items = products.map((product) => {
        const productId = product.id;
        if (product.count_direction === 'up') {
          const maxStock = originalStocks[productId] || 0;
          const position = newStocks[productId] ?? (loadedPositions[productId] ?? 1);
          const remaining = Math.max(0, maxStock - (position - 1));
          return { product_id: productId, new_stock: remaining };
        }
        const remaining = newStocks[productId] ?? (product.stock || 0);
        return { product_id: productId, new_stock: remaining };
      });

      await axios.post(`${API}/api/lotto/report-batch`, { batch_id: batchId, items }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Bestand bestätigt – Meldung gespeichert');
      fetchProducts();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getStockStatus = (product) => {
    const pos = newStocks[product.id] ?? (product.count_direction === 'up' ? 1 : (originalStocks[product.id] || 0));
    const threshold = product.warning_threshold;
    if (product.count_direction === 'up') {
      const maxStock = originalStocks[product.id] || 0;
      if (pos > maxStock) return { color: 'bg-gray-500', text: 'Ausverkauft', icon: '⚫' };
      if (pos === maxStock) return { color: 'bg-yellow-500', text: 'Letzte Karte', icon: '🟡' };
      if (pos === 1) return { color: 'bg-gray-400', text: 'Nicht gestartet', icon: '⚪' };
      return { color: 'bg-blue-500', text: 'Zählung läuft', icon: '🔵' };
    }
    const currentStock = pos;
    if (currentStock <= 0) return { color: 'bg-gray-500', text: 'Ausverkauft', icon: '⚫' };
    if (currentStock < threshold) return { color: 'bg-red-500', text: 'Kritisch', icon: '🔴' };
    if (currentStock === threshold) return { color: 'bg-yellow-500', text: 'Niedrig', icon: '🟡' };
    return { color: 'bg-green-500', text: 'Gut', icon: '🟢' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 sm:py-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Ticket className="h-4 w-4 sm:h-5 sm:w-5" />
            Alle Produkte
            <span className="ml-auto bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs sm:text-sm px-2 py-1 rounded">
              {products.length} Produkte
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {products.map(product => {
              const status = getStockStatus(product);
              const difference = getDifference(product.id);
              const isUp = product.count_direction === 'up';
              const pos = newStocks[product.id] ?? (isUp ? 1 : (originalStocks[product.id] || 0));
              const currentStock = pos; // alias for down mode
              const maxStock = originalStocks[product.id] || 0;
              const isSoldOut = isUp ? pos > maxStock : pos <= 0;   // Aus erst NACH letztem Los
              const isLastCard = isUp ? pos === maxStock : pos === 1; // letzte Karte noch da
              const isComplete = isLastCard;
              
              return (
                <div 
                  key={product.id} 
                  className={`p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors ${
                    difference !== 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  {/* Mobile & Desktop Layout */}
                  <div className="flex items-center justify-between gap-2">
                    {/* Links: Icon + Name + Badges */}
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-xl flex-shrink-0">{status.icon}</span>
                      <div className="min-w-0">
                        <div className="flex items-center flex-wrap gap-1 min-w-0">
                          <span className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate max-w-[120px]">{product.name}</span>
                          {(pendingMap[product.id] || 0) > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-300 flex-shrink-0">
                              <Clock className="h-3 w-3 flex-shrink-0" />
                              {pendingMap[product.id]}
                            </span>
                          )}
                        </div>
                        {/* Zweite Zeile: Preis + Status-Badges */}
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500 dark:text-slate-400">{new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(product.price)} €</span>
                          {difference !== 0 && (
                            <span className={`text-xs font-semibold ${difference < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {difference > 0 ? '+' : ''}{difference}
                            </span>
                          )}
                          {isComplete && (
                            <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400">✓</span>
                          )}
                          {isSoldOut && (
                            <span className="text-xs font-semibold text-gray-500">Aus</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Rechts: Buttons */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => handleDecrement5(product.id)}
                        disabled={currentStock < 5}
                        className="hidden md:block h-10 w-10 p-0 text-gray-500 dark:text-slate-400 border dark:border-slate-600 rounded disabled:opacity-30"
                      >
                        -5
                      </button>
                      <button
                        onClick={() => handleDecrement(product.id)}
                        disabled={isUp ? pos <= (loadedPositions[product.id] ?? 1) : pos <= 0}
                        data-testid={`lotto-minus-${product.id}`}
                        className="h-11 w-11 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border-2 dark:border-slate-600 rounded-lg disabled:opacity-30 touch-manipulation select-none"
                      >
                        <span className="text-xl">−</span>
                      </button>
                      
                      <div data-testid={`lotto-value-${product.id}`} className={`h-11 flex items-center justify-center rounded-lg border-2 font-bold text-base px-2 min-w-[4rem] ${
                        isSoldOut
                          ? 'bg-gray-200 border-gray-400 text-gray-500 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-400'
                          : isComplete
                            ? 'bg-yellow-100 border-yellow-500 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-500'
                            : pos > 0 && !isUp
                              ? pos < product.warning_threshold
                                ? 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-500 dark:text-red-300'
                                : 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-500 dark:text-blue-300'
                              : isUp && pos > 1
                                ? 'bg-blue-100 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-500 dark:text-blue-300'
                                : 'bg-gray-100 border-gray-300 text-gray-500 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-400'
                      }`}>
                        {isSoldOut ? 'Aus' : pos}
                      </div>
                      
                      <button
                        onClick={() => handleIncrement(product.id)}
                        disabled={isUp ? isSoldOut : pos >= originalStocks[product.id]}
                        data-testid={`lotto-plus-${product.id}`}
                        className="h-11 w-11 p-0 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 border-2 dark:border-slate-600 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation select-none"
                      >
                        <span className="text-xl">+</span>
                      </button>
                      <button
                        onClick={() => handleIncrement5(product.id)}
                        disabled={isUp ? isSoldOut : pos >= originalStocks[product.id] - 4}
                        className="hidden md:block h-10 w-10 p-0 text-gray-500 dark:text-slate-400 border dark:border-slate-600 rounded disabled:opacity-30"
                      >
                        +5
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Floating Save Button - always visible */}
      <div className="fixed bottom-20 md:bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <Button
          onClick={handleSaveAll}
          disabled={saving}
          size="lg"
          className={`h-14 px-6 md:px-8 text-white shadow-2xl rounded-full flex items-center gap-2 md:gap-3 transition-all ${
            hasChanges()
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-500 hover:bg-gray-600'
          }`}
        >
          {saving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <Package className="h-5 w-5" />
          )}
          <span className="font-semibold text-sm md:text-base">
            {saving ? 'Speichere...' : hasChanges() ? 'Alle speichern' : 'Bestand bestätigen'}
          </span>
        </Button>
      </div>
    </div>
  );
};


// Sortable Product Card Component
const SortableProductCard = ({ product, getStockStatus, openEditDialog, openRestockDialog, handleDelete, pendingPackages }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const status = getStockStatus(product);

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`${status.color === 'bg-red-500' || status.color === 'bg-gray-500' ? 'border-2 border-red-400' : ''} ${isDragging ? 'shadow-2xl' : ''}`}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none">
                <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400" />
              </div>
              <span className="flex items-center gap-2">
                {status.icon} {product.name}
              </span>
            </div>
            <span className="text-lg font-bold text-green-600">{new Intl.NumberFormat('de-DE',{minimumFractionDigits:2}).format(product.price)} €</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className={`p-3 rounded-lg ${status.color === 'bg-red-500' || status.color === 'bg-gray-500' ? 'bg-red-50 dark:bg-red-900/20' : status.color === 'bg-yellow-500' ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Bestand: {product.stock} Stück</p>
            <p className="text-xs text-gray-600 dark:text-slate-400">Warnschwelle: {product.warning_threshold} Stück</p>
            {(product.stock <= product.warning_threshold && product.stock > 0) && (
              <div className="flex items-center gap-1 mt-1 text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs font-medium">NIEDRIG!</span>
              </div>
            )}
            {product.stock === 0 && (
              <div className="flex items-center gap-1 mt-1 text-red-700 dark:text-red-400">
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs font-medium">AUSVERKAUFT – Neues Paket erfassen!</span>
              </div>
            )}
            {pendingPackages > 0 && (
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-amber-200 dark:border-amber-700/50" data-testid="pending-order-badge">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-600">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  {pendingPackages} Pkt. unterwegs
                </span>
              </div>
            )}
          </div>

          {product.package_number && (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <p className="font-medium">📦 Paketnummer:</p>
              <p className="font-mono mt-1 break-all">{product.package_number}</p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => openEditDialog(product)}
              className="flex-1"
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Bearbeiten
            </Button>
            {product.stock === 0 && (
              <Button
                size="sm"
                onClick={() => openRestockDialog(product)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="restock-button"
              >
                <Package className="h-3 w-3 mr-1" />
                Neues Paket
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDelete(product.id)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
